/**
 * <s-recently-viewed> — Remembers the product handles a shopper views (localStorage)
 * and renders up to `limit` of them, excluding the current product, by fetching each
 * product's `?view=recent-card` alternate template (one <li> card in theme markup).
 *
 * Element attributes:
 *   current-handle — Handle of the product on this page; saved to history after rendering.
 *   limit          — Max cards to show (default 6).
 *
 * Required child markup:
 *   [data-recently-viewed-list] — <ul> that receives the fetched <li> cards.
 *
 * The element starts `hidden` and is revealed only when at least one card renders.
 */
const STORAGE_KEY = 'tss:recently-viewed';
const MAX_STORED = 12;

function readHistory() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

function writeHistory(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
  } catch {
    /* Storage can be unavailable (private mode); the section simply stays hidden. */
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'INR';
  const locale = document.documentElement.lang || 'en-IN';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2);
  }
}

function imageAt(src, width) {
  if (!src) return '';
  const url = new URL(src.startsWith('//') ? `https:${src}` : src, location.origin);
  url.searchParams.set('width', String(width));
  return url.toString();
}

/** Fallback card from /products/<handle>.js, mirroring the theme's product card markup. */
function cardFromJson(product) {
  const image = product.featured_image
    ? `<img src="${escapeHtml(imageAt(product.featured_image, 550))}" srcset="${[375, 550, 750].map((w) => `${escapeHtml(imageAt(product.featured_image, w))} ${w}w`).join(', ')}" sizes="(min-width: 1024px) 16vw, (min-width: 768px) 33vw, 50vw" alt="${escapeHtml(product.title)}" loading="lazy" class="block aspect-[3/4] size-full object-cover motion-safe:transition-transform motion-safe:duration-[1200ms] ease-out group-hover:scale-105">`
    : '<span class="block aspect-[3/4]"></span>';
  const price = product.price_varies ? `${formatMoney(product.price_min)} – ${formatMoney(product.price_max)}` : formatMoney(product.price);
  const li = document.createElement('li');
  li.className = 'm-0 min-w-0';
  li.innerHTML = `<div class="group flex flex-col gap-1.5 text-center">
    <a href="${escapeHtml(product.url)}" class="relative block overflow-hidden bg-surface-secondary" aria-label="${escapeHtml(product.title)}">${image}<span class="pointer-events-none absolute inset-2 border border-[#FFF1E9]/60" aria-hidden="true"></span></a>
    <div class="flex flex-col items-center gap-1 px-1">
      <h3 class="font-heading text-on-surface line-clamp-2 text-balance leading-snug text-base"><a href="${escapeHtml(product.url)}" class="no-underline text-on-surface">${escapeHtml(product.title)}</a></h3>
      <p class="m-0 text-[0.6875rem] tracking-[0.14em] text-on-surface-secondary">${escapeHtml(price)}</p>
    </div>
  </div>`;
  return li;
}

class SRecentlyViewed extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const current = this.getAttribute('current-handle') || '';
    const limit = parseInt(this.getAttribute('limit') || '6', 10);
    const history = readHistory();
    const toShow = history.filter((h) => h !== current).slice(0, limit);

    if (current) writeHistory([current, ...history.filter((h) => h !== current)]);
    if (toShow.length) this.#render(toShow);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #render(handles) {
    const list = this.querySelector('[data-recently-viewed-list]');
    if (!list) return;
    const root = window.Shopify?.routes?.root || '/';
    const signal = this.#ac.signal;
    const loadCard = async (handle) => {
      const base = `${root}products/${encodeURIComponent(handle)}`;
      try {
        const res = await fetch(`${base}?view=recent-card`, { signal });
        if (res.status === 404) return null;
        if (res.ok) {
          const template = document.createElement('template');
          template.innerHTML = (await res.text()).trim();
          const card = template.content.querySelector('[data-recent-card]');
          if (card) return card;
        }
        const json = await fetch(`${base}.js`, { signal });
        if (!json.ok) return null;
        const product = await json.json();
        return product?.url ? cardFromJson(product) : null;
      } catch {
        return undefined;
      }
    };
    const results = await Promise.all(handles.map(loadCard));
    if (signal.aborted) return;
    const missing = [];
    const fragment = document.createDocumentFragment();
    results.forEach((card, i) => {
      if (card) fragment.append(card);
      else if (card === null) missing.push(handles[i]);
    });
    if (missing.length) writeHistory(readHistory().filter((h) => !missing.includes(h)));
    if (!fragment.childElementCount) return;
    list.replaceChildren(fragment);
    this.hidden = false;
  }
}

if (!customElements.get('s-recently-viewed')) customElements.define('s-recently-viewed', SRecentlyViewed);
